/**
 * URL Slug Mapping Utility
 *
 * Maps German URL slugs to their localized equivalents in English.
 * Used for routing, URL generation, and redirect handling.
 *
 * This is the SINGLE SOURCE OF TRUTH for all URL localization.
 */

import { branchSlugMapping } from "./branch-slug-mapping";

export const SUPPORTED_LANGUAGES = ["de", "en"] as const;
export type SupportedLanguage = (typeof SUPPORTED_LANGUAGES)[number];

/** Display labels for each supported language, shown in the language's own name. */
export const LANGUAGE_LABELS: Record<SupportedLanguage, string> = {
    de: "Deutsch",
    en: "English",
};

// Mapping of German slugs to localized slugs for each language
export type SlugMapping = Record<string, Record<SupportedLanguage, string>>;

/**
 * Category slugs (top-level paths)
 * These are the main sections of the website
 */
export const categorySlugMapping: SlugMapping = {
    // Relaunch Eckpfeiler (Nav 6-Punkt). Zielsegmente fuer Phase 3; die Nav
    // laeuft uebergangsweise noch ueber bestehende Routen. `angebote` bewusst
    // `offerings` (nicht `services`) — `dienstleistungen` haelt bereits `services`.
    angebote: {
        de: "angebote",
        en: "offerings",
    },
    // Seit dem 18.9.2026 ein Kind von `angebote → offerings`, nicht mehr top-level
    // (Entscheid Jan): `/angebote/ki-anwendungen` → `/en/offerings/ai-applications`.
    // Der Eintrag bleibt unveraendert — beide Segmente mappen einzeln, deshalb faellt
    // die EN-URL des Umzugs ohne neuen Eintrag heraus.
    "ki-anwendungen": {
        de: "ki-anwendungen",
        en: "ai-applications",
    },
    // Detailseite «Intelligente Assistenten» (Endgame § 4 / Blocker B5). Der Katalog
    // auf /angebote darf keinen Eintrag ohne Live-Detailseite fuehren (D9) — diese
    // Seite ist das Ziel.
    assistenten: {
        de: "assistenten",
        en: "assistants",
    },
    // /fuer-agenten — the Agentic-SEO demonstration page (Phase-3 Etappe 5b). A top-level
    // slug (not an angebote child), so it lives among the category-level slugs.
    "fuer-agenten": {
        de: "fuer-agenten",
        en: "for-agents",
    },
    referenzen: {
        de: "referenzen",
        en: "references",
    },
    kunden: {
        de: "kunden",
        en: "customers",
    },
    "ueber-uns": {
        de: "ueber-uns",
        en: "about-us",
    },
    produkte: {
        de: "produkte",
        en: "products",
    },
    loesungen: {
        de: "loesungen",
        en: "solutions",
    },
    dienstleistungen: {
        de: "dienstleistungen",
        en: "services",
    },
    module: {
        de: "module",
        en: "modules",
    },
    agb: {
        de: "agb",
        en: "terms",
    },
    datenschutz: {
        de: "datenschutz",
        en: "privacy",
    },
    partner: {
        de: "partner",
        en: "partners",
    },
    jobs: {
        de: "jobs",
        en: "careers",
    },
    blog: {
        de: "blog",
        en: "blog",
    },
    branchen: {
        de: "branchen",
        en: "industries",
    },
    branche: {
        de: "branche",
        en: "industry",
    },
    plattform: {
        de: "plattform",
        en: "platform",
    },
    platform: {
        de: "platform",
        en: "platform",
    },
    "success-stories": {
        de: "success-stories",
        en: "success-stories",
    },
};

/**
 * Page-specific slugs (sub-paths)
 * These are specific pages within the main categories
 */
export const pageSlugMapping: SlugMapping = {
    // Solution pages
    "vertriebs-und-auftragsverwaltung": {
        de: "vertriebs-und-auftragsverwaltung",
        en: "sales-and-order-management",
    },
    "digitaler-kundendienst": {
        de: "digitaler-kundendienst",
        en: "digital-customer-service",
    },
    "custom-cds-portal": {
        de: "custom-cds-portal",
        en: "custom-cds-portal",
    },

    // Angebote — Individualsoftware & Systemintegration (Phase-3 Etappe 5a). Child of
    // the `angebote → offerings` parent; segment-mappable, so D2-safe (like the
    // custom-cds-portal precedent). `custom software` is the EN term, not `individual
    // software`.
    individualsoftware: {
        de: "individualsoftware",
        en: "custom-software",
    },
    // Angebote — Agentic SEO (Phase-3 Etappe 5b). Child of `angebote → offerings`;
    // `agentic-seo` is a technical term, so the EN slug is identical. Segment-mappable,
    // D2-safe.
    "agentic-seo": {
        de: "agentic-seo",
        en: "agentic-seo",
    },
    // Angebote — System Engineering (Relaunch Phase 3, Etappe 3). Child of `angebote →
    // offerings`; `system engineering` is an established English technical term, so the EN
    // slug is identical (like agentic-seo). Segment-mappable, D2-safe — this makes
    // /en/offerings/system-engineering a real localized SSG route.
    "system-engineering": {
        de: "system-engineering",
        en: "system-engineering",
    },
    // Agentic Development (Phase-3 Methodenseite). Top-level slug, not an /angebote
    // child; `Agentic Development` is a technical term, so the EN slug is identical.
    // Segment-mappable, D2-safe (like agentic-seo and fuer-agenten).
    "agentic-development": {
        de: "agentic-development",
        en: "agentic-development",
    },
    // Service pages. fachliche-individualisierung + integration-consulting are retired
    // (consolidated into /angebote/individualsoftware, Etappe 5a) — their slug entries
    // stay so the old URLs still resolve to a segment (redirect source), and the
    // url-slug-mapping tests keep asserting the historical mapping.
    "fachliche-individualisierung": {
        de: "fachliche-individualisierung",
        en: "functional-customization",
    },
    "integration-consulting": {
        de: "integration-consulting",
        en: "integration-consulting",
    },
    "ki-potenzialanalyse": {
        de: "ki-potenzialanalyse",
        en: "ai-potential-analysis",
    },
    "ki-agenten-workshop": {
        de: "ki-agenten-workshop",
        en: "ai-agent-workshop",
    },
    "ki-technologie": {
        de: "ki-technologie",
        en: "ai-technology",
    },
    // Kinder von /angebote/ki-anwendungen (11.9.2026, umgezogen 18.9.2026). Das
    // Elternsegment traegt das «KI» bereits, deshalb heissen die Kinder «risiken» und
    // «potenzial» statt «ki-risiken» und «ki-potenzial». Die kurzen Einstiegs-URLs
    // /ki-risiken und /ki-potenzial fuehren als 301 hierher (siehe redirects.ts).
    risiken: {
        de: "risiken",
        en: "risks",
    },
    potenzial: {
        de: "potenzial",
        en: "potential",
    },

    // Kampagnen-Landeseiten unter /umfrage-unternehmen/ — das erste Segment ist
    // ein reiner Sammelpfad ohne eigene Seite (Entscheid Jan, 17.9.2026).
    "umfrage-unternehmen": {
        de: "umfrage-unternehmen",
        en: "company-survey",
    },
    // Kampagnen-Landeseiten unter /kampagne/ (QUAWEB-40, 15.10.2026) — ebenfalls
    // ein reiner Sammelpfad ohne eigene Seite.
    kampagne: {
        de: "kampagne",
        en: "campaign",
    },
    // `ausschreibungen` liegt seit dem 15.10.2026 unter /kampagne/ (vorher
    // /angebote/ki-anwendungen/ und /umfrage-unternehmen/). Der Eintrag bleibt
    // hier stehen, weil die Zuordnung segmentweise greift und vom Elternpfad
    // unabhaengig ist.
    ausschreibungen: {
        de: "ausschreibungen",
        en: "tenders",
    },
    ergebnisse: {
        de: "ergebnisse",
        en: "results",
    },

    // Product pages
    "vertriebsportal-starter": {
        de: "vertriebsportal-starter",
        en: "sales-portal-starter",
    },
    "vertriebsportal-business": {
        de: "vertriebsportal-business",
        en: "sales-portal-business",
    },
    "vertriebsportal-professional": {
        de: "vertriebsportal-professional",
        en: "sales-portal-professional",
    },
    "konto-erstellen": {
        de: "konto-erstellen",
        en: "create-account",
    },
    // VP sub-pages
    features: {
        de: "features",
        en: "features",
    },
    anpassen: {
        de: "anpassen",
        en: "customize",
    },
    win: {
        de: "win",
        en: "win",
    },

    // Platform sub-pages
    product: {
        de: "product",
        en: "product",
    },
    technology: {
        de: "technology",
        en: "technology",
    },

    // Module pages
    cpq: {
        de: "cpq",
        en: "cpq",
    },
    appointments: {
        de: "appointments",
        en: "appointments",
    },
    messaging: {
        de: "messaging",
        en: "messaging",
    },
    rechnungsstellung: {
        de: "rechnungsstellung",
        en: "invoicing",
    },
    iam: {
        de: "iam",
        en: "iam",
    },
    "profile-management": {
        de: "profile-management",
        en: "profile-management",
    },
    analytics: {
        de: "analytics",
        en: "analytics",
    },

    // Vertriebsportal sub-pages
    vertriebsportal: {
        de: "vertriebsportal",
        en: "sales-portal",
    },
    "ki-dienstleistungs-konfigurator-generator": {
        de: "ki-dienstleistungs-konfigurator-generator",
        en: "ai-service-configurator-generator",
    },
    // Phase-3 Etappe 2: the Offerten-Generator page slug under /angebote/ki-anwendungen.
    // The EN slug maps segment-wise (offerten-generator → quote-generator), which is
    // exactly what lets the EN URL be derived from the DE URL — unlike the old VP
    // playground, whose edition sat in no segment (Story D2).
    "offerten-generator": {
        de: "offerten-generator",
        en: "quote-generator",
    },
    // Phase-3 Etappe 3: DaaS page slug under /angebote/ki-anwendungen. The term is identical in
    // DE and EN (technical label), so the EN slug is the same — it maps segment-wise,
    // like the custom-cds-portal precedent, D2-safe.
    "document-as-a-service": {
        de: "document-as-a-service",
        en: "document-as-a-service",
    },
    "dienstleistungs-katalog": {
        de: "dienstleistungs-katalog",
        en: "service-catalog",
    },

    // Other pages
    technologie: {
        de: "technologie",
        en: "technology",
    },
    // Kinder des CPQ-Moduls (Endgame § 10: «Playground + Katalog anteasern und
    // verlinken»). Beide Seiten lagen seit dem VP-Teardown unrouted im Repo und sind
    // unter `/module/cpq/*` zurueck. Ohne diese Eintraege leitet die segment-weise
    // EN-Ableitung den deutschen Slug unveraendert weiter.
    playground: {
        de: "playground",
        en: "playground",
    },
    katalog: {
        de: "katalog",
        en: "catalogue",
    },
    // Technologie-Detailseite webbloqs (Technologie-Schaufenster, Etappe 2). Child of
    // `technologie → technology`; `webbloqs` ist ein Markenname, in DE und EN identisch. Der
    // Eintrag hält den Slug für Test-Konsistenz und die segment-weise EN-URL-Ableitung.
    webbloqs: {
        de: "webbloqs",
        en: "webbloqs",
    },
    // Technologie-Detailseite Magellan (Technologie-Schaufenster, Etappe 3). Child of
    // `technologie → technology`; `magellan` ist ein Markenname, in DE und EN identisch. Der
    // Eintrag hält den Slug für Test-Konsistenz und die segment-weise EN-URL-Ableitung.
    magellan: {
        de: "magellan",
        en: "magellan",
    },
    // Technologie-Detailseiten Agent-Skills + Plot (Technologie-Schaufenster, Etappe 4). Kinder
    // von `technologie → technology`; `agent-skills` und `plot` sind Markennamen, in DE und EN
    // identisch. Die Einträge halten die Slugs für Test-Konsistenz und die segment-weise
    // EN-URL-Ableitung.
    "agent-skills": {
        de: "agent-skills",
        en: "agent-skills",
    },
    plot: {
        de: "plot",
        en: "plot",
    },
    // Technologie-Detailseite AppShell (Technologie-Schaufenster, Etappe 5). Kind von
    // `technologie → technology`; `appshell` ist ein Markenname, in DE und EN identisch. Der
    // Eintrag hält den Slug für Test-Konsistenz und die segment-weise EN-URL-Ableitung.
    appshell: {
        de: "appshell",
        en: "appshell",
    },
    // Technologie-Detailseite websmith (Nachzügler zum Technologie-Schaufenster). Kind von
    // `technologie → technology`; `websmith` ist ein Markenname, in DE und EN identisch. Der
    // Eintrag hält den Slug für Test-Konsistenz und die segment-weise EN-URL-Ableitung.
    websmith: {
        de: "websmith",
        en: "websmith",
    },
    produkt: {
        de: "produkt",
        en: "product",
    },
};

/**
 * Get the localized slug for a given German slug and language
 * @param germanSlug The German slug (e.g., "kunden")
 * @param locale The target locale ("de" or "en")
 * @returns The localized slug, or the original if no mapping exists
 */
/**
 * Removes a leading `/de` or `/en` locale segment only when it is the first path segment.
 * (A naive `^/(de|en)` strip would corrupt `/demos/...` by matching the `de` inside `demos`.)
 */
export const stripOptionalLanguagePrefixFromPath = (pathOnly: string): string => {
    const normalized = pathOnly.startsWith("/") ? pathOnly : `/${pathOnly}`;
    const segments = normalized.split("/").filter(Boolean);
    if (segments.length === 0) {
        return "/";
    }
    const [first, ...rest] = segments;
    if (first === "de" || first === "en") {
        return rest.length === 0 ? "/" : `/${rest.join("/")}`;
    }
    return normalized;
};

/** Paths served from `public/` at a single canonical URL — must not gain an `/en` prefix. */
const LOCALE_AGNOSTIC_PUBLIC_PREFIXES = ["/demos", "/static", "/images", "/fonts", "/assets"] as const;

/**
 * Erkennt alles, was gar kein Pfad dieser Website ist: Schema-URIs (`mailto:`,
 * `tel:`), absolute URLs (`https://…`) und protokollrelative (`//cdn…`).
 *
 * Gefunden 30.7.2026: ohne diese Pruefung behandelte `translateUrl` auch
 * `mailto:michael@quatico.com` als Pfad. Da darin kein `/` vorkommt, blieb es
 * ein einzelnes Segment, lief unuebersetzt durch und bekam am Ende das
 * Sprachpraefix — `/en/mailto:michael@quatico.com`, ein Link ins Leere. Auf
 * 41 EN-Seiten betraf das 42 mailto- und 41 tel-Links, ueberall wo eine
 * Kontakt-Sektion steht.
 *
 * `#kontakt` ist bewusst NICHT hier: In-Page-Anker sollen weiterhin durch die
 * Lokalisierung laufen (sie haengen an einem Pfad, der uebersetzt wird).
 */
const NON_PATH_URL = /^(?:[a-z][a-z0-9+.-]*:|\/\/)/i;

export const shouldBypassUrlLocalization = (url: string): boolean => {
    if (NON_PATH_URL.test(url)) {
        return true;
    }
    const path = url.split("?")[0].split("#")[0];
    return LOCALE_AGNOSTIC_PUBLIC_PREFIXES.some(root => path === root || path.startsWith(`${root}/`));
};

export const getLocalizedSlug = (germanSlug: string, locale: SupportedLanguage): string => {
    // Try category mapping first
    const categoryMapping = categorySlugMapping[germanSlug];
    if (categoryMapping) {
        return categoryMapping[locale] || germanSlug;
    }

    // Try page mapping
    const pageMapping = pageSlugMapping[germanSlug];
    if (pageMapping) {
        return pageMapping[locale] || germanSlug;
    }

    // Try branch mapping
    const branchMapping = branchSlugMapping[germanSlug];
    if (branchMapping) {
        return branchMapping[locale] || germanSlug;
    }

    // If no mapping found, return the original slug
    return germanSlug;
};

/**
 * Get the German slug from a localized slug
 * @param localizedSlug The localized slug (e.g., "customers")
 * @param locale The source locale ("de" or "en")
 * @returns The German slug, or the original if no mapping exists
 */
export const getGermanSlug = (localizedSlug: string, locale: SupportedLanguage): string => {
    // Try category mapping
    for (const [germanSlug, mappings] of Object.entries(categorySlugMapping)) {
        if (mappings[locale] === localizedSlug) {
            return germanSlug;
        }
    }

    // Try page mapping
    for (const [germanSlug, mappings] of Object.entries(pageSlugMapping)) {
        if (mappings[locale] === localizedSlug) {
            return germanSlug;
        }
    }

    // Try branch mapping
    for (const [germanSlug, mappings] of Object.entries(branchSlugMapping)) {
        if (mappings[locale] === localizedSlug) {
            return germanSlug;
        }
    }

    // If no mapping found, assume it's already a German slug
    return localizedSlug;
};

/**
 * Translate a full URL path from one language to another
 * @param url The URL path (e.g., "/produkte/vertriebsportal-starter")
 * @param sourceLocale The source language
 * @param targetLocale The target language
 * @returns The translated URL path
 */
export const translateUrl = (url: string, sourceLocale: SupportedLanguage, targetLocale: SupportedLanguage): string => {
    if (sourceLocale === targetLocale) {
        return url;
    }

    let pathOnly = url;
    let suffix = "";
    const hashIndex = pathOnly.indexOf("#");
    if (hashIndex >= 0) {
        suffix += pathOnly.slice(hashIndex);
        pathOnly = pathOnly.slice(0, hashIndex);
    }
    const searchIndex = pathOnly.indexOf("?");
    if (searchIndex >= 0) {
        suffix = pathOnly.slice(searchIndex) + suffix;
        pathOnly = pathOnly.slice(0, searchIndex);
    }

    const cleanUrl = stripOptionalLanguagePrefixFromPath(pathOnly);

    // Split the URL into segments
    const segments = cleanUrl.split("/").filter(Boolean);

    // Translate each segment
    const translatedSegments = segments.map(segment => {
        // First convert to German (canonical form)
        const germanSlug = sourceLocale === "de" ? segment : getGermanSlug(segment, sourceLocale);

        // Then translate to target language
        return getLocalizedSlug(germanSlug, targetLocale);
    });

    // Reconstruct the URL
    const translatedPath = "/" + translatedSegments.join("/");

    // Add language prefix for non-German languages
    if (targetLocale === "de") {
        return translatedPath + suffix;
    }
    return `/${targetLocale}${translatedPath}` + suffix;
};

/**
 * Get the localized URL for a given German URL and language
 * @param germanUrl The German URL (e.g., "/kunden" or "/produkte/vertriebsportal-starter")
 * @param locale The target locale ("de" or "en")
 * @returns The localized URL with language prefix if needed
 */
export const getLocalizedUrl = (germanUrl: string, locale: SupportedLanguage): string => {
    return translateUrl(germanUrl, "de", locale);
};

/**
 * Get the German URL from a localized URL
 * @param localizedUrl The localized URL (e.g., "/en/customers" or "/customers")
 * @param sourceLocale The source locale ("de" or "en")
 * @returns The German canonical URL without language prefix
 */
export const getGermanUrl = (localizedUrl: string, sourceLocale: SupportedLanguage): string => {
    return translateUrl(localizedUrl, sourceLocale, "de");
};

/**
 * Get all German category slugs
 * @returns Array of German category slugs
 */
export const getAllGermanCategorySlugs = (): string[] => {
    return Object.keys(categorySlugMapping);
};

/**
 * Get all German page slugs
 * @returns Array of German page slugs
 */
export const getAllGermanPageSlugs = (): string[] => {
    return Object.keys(pageSlugMapping);
};

/**
 * Get all localized category slugs for a specific language
 * @param locale The target locale ("de" or "en")
 * @returns Array of localized category slugs
 */
export const getAllLocalizedCategorySlugs = (locale: SupportedLanguage): string[] => {
    return Object.values(categorySlugMapping).map(mapping => mapping[locale] || mapping.de);
};

/**
 * Get all localized page slugs for a specific language
 * @param locale The target locale ("de" or "en")
 * @returns Array of localized page slugs
 */
export const getAllLocalizedPageSlugs = (locale: SupportedLanguage): string[] => {
    return Object.values(pageSlugMapping).map(mapping => mapping[locale] || mapping.de);
};
